/**
 * Onboarding step identifiers: we use step hashes (labels) in API payloads,
 * e.g. ["basic-info", "profile-info", "store-details", ...] not field names.
 */

/** Legacy backend IDs → step hash (for reading old API responses). */
const LEGACY_ID_TO_HASH = {
  profile_info: "basic-info",
  social_media: "profile-info",
  store_details: "store-details",
  dots_account: "dots-account",
  shopify_accounts: "shopify-details",
  agreement: "agreement",
};

/**
 * Map API response to frontend completion map.
 * API uses step hashes (e.g. "basic-info", "profile-info"). Also accepts legacy ids for backward compatibility.
 * @param {string[]} stepIdsOrHashes - Array from API (step hashes or legacy field names)
 * @param {Array} stepConfig - STORE_ONBOARDING_STEP_CONFIG
 * @returns {Object} - { [hash]: true } for completed steps
 */
export const mapBackendStepsToFrontend = (
  stepIdsOrHashes,
  stepConfig = STORE_ONBOARDING_STEP_CONFIG
) => {
  if (!Array.isArray(stepIdsOrHashes)) {
    return {};
  }

  const completedSteps = {};
  const validHashes = new Set(stepConfig.map((s) => s.hash));

  stepIdsOrHashes.forEach((idOrHash) => {
    if (validHashes.has(idOrHash)) {
      completedSteps[idOrHash] = true;
    } else if (LEGACY_ID_TO_HASH[idOrHash]) {
      completedSteps[LEGACY_ID_TO_HASH[idOrHash]] = true;
    }
  });

  return completedSteps;
};

/**
 * Get completed step hashes (labels) for onboarding_completed_steps API payload.
 * Sends step labels only, e.g. ["basic-info", "profile-info", "store-details"].
 */
export const getCompletedStepHashes = (completionMap, stepConfig = STORE_ONBOARDING_STEP_CONFIG) => {
  if (!completionMap || typeof completionMap !== "object") return [];
  return stepConfig.filter((step) => completionMap[step.hash]).map((step) => step.hash);
};

/**
 * Step hash for the current step (single label to add to completed list).
 */
export const getStepHashForStep = (stepHash) => stepHash || null;

/**
 * Configurable onboarding steps (order: Agreement first, then Basic Info → … → Shopify Details).
 * Agreement must be completed before any other step is enabled.
 * onboarding_completed_steps in API use these step hashes (labels), e.g. "agreement", "basic-info", "profile-info".
 */
export const STORE_ONBOARDING_STEP_CONFIG = [
  { hash: "agreement", label: "Agreement" },
  { hash: "basic-info", label: "Basic Info" },
  { hash: "profile-info", label: "Profile Info" },
  { hash: "store-details", label: "Store Details" },
  { hash: "dots-account", label: "Dots Account" },
  { hash: "communications", label: "Communications" },
  { hash: "shopify-details", label: "Shopify Details" },
];
